import React from 'react';
import { 
  UsersIcon, 
  GlobeAltIcon, 
  CalendarIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Tenant } from './TenantTable';

interface TenantCardProps {
  tenant: Tenant;
  onView: () => void;
  onSuspend: () => void;
  onDelete: () => void;
  onManageSubscription: () => void;
  isSelected?: boolean;
  onSelect?: () => void;
}

const TenantCard: React.FC<TenantCardProps> = ({
  tenant,
  onView,
  onSuspend,
  onDelete,
  onManageSubscription,
  isSelected = false,
  onSelect
}) => {
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
      case 'trial':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'past_due':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'cancelled':
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
      case 'suspended':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  const getTierColor = (tier?: string): string => {
    switch (tier) {
      case 'starter':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'professional':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'enterprise':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'custom':
        return 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-4 w-4 text-emerald-400" />;
      case 'past_due':
      case 'suspended':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-400" />;
      default:
        return <CheckCircleIcon className="h-4 w-4 text-slate-400" />;
    }
  };

  const formatCurrency = (amount?: number): string => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getHealthScore = (): { score: number; color: string; status: string } => {
    let score = 100;
    
    // Deduct for status issues
    if (tenant.subscription_status === 'past_due') score -= 40;
    if (tenant.subscription_status === 'suspended') score -= 60;
    if (tenant.subscription_status === 'cancelled') score -= 80;
    
    // Deduct for inactivity
    if (!tenant.last_login_at) score -= 20;
    else {
      const daysSinceActivity = Math.floor((Date.now() - new Date(tenant.last_login_at).getTime()) / (1000 * 60 * 60 * 24));
      if (daysSinceActivity > 30) score -= 30;
      else if (daysSinceActivity > 7) score -= 15;
    }
    
    // Simple score based on role
    if (tenant.role === 'admin') score += 10;
    else if (!tenant.role || tenant.role === 'user') score -= 5;

    if (score >= 80) return { score, color: 'text-emerald-400', status: 'Healthy' };
    if (score >= 60) return { score, color: 'text-amber-400', status: 'Warning' };
    return { score, color: 'text-red-400', status: 'At Risk' };
  };

  const health = getHealthScore();

  return (
    <div className={`bg-slate-800/50 backdrop-blur-sm rounded-xl border-2 shadow-xl hover:shadow-2xl transition-all duration-200 ${
      isSelected ? 'border-indigo-500/50 bg-indigo-500/10' : 'border-slate-700/50'
    }`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              {onSelect && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={onSelect}
                  className="rounded border-slate-600 bg-slate-700 text-indigo-500 focus:ring-indigo-500/50"
                />
              )}
              <h3 className="text-lg font-semibold text-white truncate">
                {tenant.full_name || `${tenant.first_name} ${tenant.last_name}` || tenant.email}
              </h3>
            </div>
            <div className="flex items-center space-x-1 mt-1">
              <GlobeAltIcon className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-400">{tenant.email}</span>
            </div>
            {tenant.tenant_name && (
              <div className="text-xs text-slate-500 mt-1">Tenant: {tenant.tenant_name}</div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {getStatusIcon(tenant.subscription_status)}
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(tenant.subscription_status)}`}>
              {tenant.subscription_status}
            </span>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <UsersIcon className="h-5 w-5 text-slate-400" />
            <div>
              <div className="text-sm font-medium text-white">
                {tenant.role || 'user'}
              </div>
              <div className="text-xs text-slate-400">Role</div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <CurrencyDollarIcon className="h-5 w-5 text-slate-400" />
            <div>
              <div className="text-sm font-medium text-white">
                {formatCurrency(tenant.monthly_fee)}
              </div>
              <div className="text-xs text-slate-400">Monthly Fee</div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <CalendarIcon className="h-5 w-5 text-slate-400" />
            <div>
              <div className="text-sm font-medium text-white">
                {formatDate(tenant.created_at)}
              </div>
              <div className="text-xs text-slate-400">Created</div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <ChartBarIcon className="h-5 w-5 text-slate-400" />
            <div>
              <div className={`text-sm font-medium ${health.color}`}>
                {health.score}%
              </div>
              <div className="text-xs text-slate-400">{health.status}</div>
            </div>
          </div>
        </div>

        {/* Subscription Tier */}
        {tenant.subscription_tier && (
          <div className="mb-4">
            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getTierColor(tenant.subscription_tier)}`}>
              {tenant.subscription_tier.charAt(0).toUpperCase() + tenant.subscription_tier.slice(1)} Plan
            </span>
          </div>
        )}

        {/* Last Login */}
        <div className="mb-4 p-3 bg-slate-700/30 rounded-xl border border-slate-600/30">
          <div className="text-xs text-slate-400 mb-1">Last Login</div>
          <div className="text-sm font-medium text-slate-300">
            {formatDate(tenant.last_login_at)}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={onView}
            className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-medium py-2 px-3 rounded-xl transition-colors duration-200"
          >
            View Details
          </button>
          <button
            onClick={onManageSubscription}
            className="flex-1 bg-purple-500 hover:bg-purple-600 text-white text-xs font-medium py-2 px-3 rounded-xl transition-colors duration-200"
          >
            Manage
          </button>
        </div>

        <div className="flex space-x-2 mt-2">
          <button
            onClick={onSuspend}
            className="flex-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium py-2 px-3 rounded-xl transition-colors duration-200"
          >
            {tenant.subscription_status === 'suspended' ? 'Reactivate' : 'Suspend'}
          </button>
          <button
            onClick={onDelete}
            className="flex-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium py-2 px-3 rounded-xl transition-colors duration-200"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Health Indicator Bar */}
      <div className="px-6 pb-4">
        <div className="w-full bg-slate-700/50 rounded-full h-2 border border-slate-600/30">
          <div 
            className={`h-2 rounded-full transition-all duration-300 ${
              health.score >= 80 ? 'bg-emerald-400' : 
              health.score >= 60 ? 'bg-amber-400' : 'bg-red-400'
            }`}
            style={{ width: `${health.score}%` }}
          ></div>
        </div>
        <div className="text-xs text-slate-400 mt-1 text-center">
          Health Score: {health.score}%
        </div>
      </div>
    </div>
  );
};

export default TenantCard;